import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createChart, ColorType, IChartApi, AreaData, Time } from 'lightweight-charts'
import { apiFetch, journalApiFetch } from '../lib/api'
import AskKitChat from '../components/AskKitChat'
import ConnectionManager from '../components/ConnectionManager'

// ============================================================================
// TYPES
// ============================================================================

interface TradingAccount {
  id: string
  name: string
  broker?: string
  accountType: 'live' | 'demo' | 'prop_firm'
  currency: string
  initialBalance: number
  currentBalance: number
  propFirmName?: string
  totalTrades: number
  winningTrades: number
  losingTrades: number
  totalPnL: number
  winRate: number
  profitFactor: number
}

interface JournalEntry {
  id: string
  accountId: string
  symbol: string
  direction: 'LONG' | 'SHORT'
  entryPrice: number
  exitPrice?: number
  quantity: number
  stopLoss?: number
  takeProfit?: number
  riskAmount?: number
  rMultiple?: number
  setup?: string
  emotion?: string
  mistake?: string  // Trading mistake tracking
  entryTime: string
  exitTime?: string
  notes?: string
  pnl?: number
  pnlPercent?: number
  isWin?: boolean
  status: 'open' | 'closed'
  tags?: string
  screenshotUrl?: string  // Screenshot attachment
}

// Common trading mistakes for tracking
const TRADING_MISTAKES = {
  none: { label: 'No Mistake', emoji: '✅', color: 'text-emerald-400' },
  fomo: { label: 'FOMO Entry', emoji: '😱', color: 'text-yellow-400' },
  revenge: { label: 'Revenge Trade', emoji: '😤', color: 'text-rose-400' },
  oversize: { label: 'Oversized Position', emoji: '📈', color: 'text-orange-400' },
  no_stop: { label: 'No Stop Loss', emoji: '🚫', color: 'text-rose-500' },
  moved_stop: { label: 'Moved Stop Loss', emoji: '↔️', color: 'text-yellow-500' },
  early_exit: { label: 'Exited Too Early', emoji: '⏱️', color: 'text-blue-400' },
  late_exit: { label: 'Exited Too Late', emoji: '🐌', color: 'text-purple-400' },
  chasing: { label: 'Chased Price', emoji: '🏃', color: 'text-orange-400' },
  ignored_plan: { label: 'Ignored Trading Plan', emoji: '📋', color: 'text-rose-400' },
  overtrading: { label: 'Overtrading', emoji: '🔄', color: 'text-yellow-400' },
  news_trade: { label: 'Traded Into News', emoji: '📰', color: 'text-purple-400' },
  tired: { label: 'Traded While Tired', emoji: '😴', color: 'text-gray-400' },
} as const

interface PerformanceMetrics {
  totalTrades: number
  winningTrades: number
  losingTrades: number
  winRate: number
  totalPnL: number
  grossProfit: number
  grossLoss: number
  averageWin: number
  averageLoss: number
  largestWin: number
  largestLoss: number
  profitFactor: number
  expectancy: number
  averageRMultiple: number
  longestWinStreak: number
  longestLoseStreak: number
  currentStreak: number
  currentStreakType: 'win' | 'lose' | 'none'
}

interface EquityPoint {
  date: string
  balance: number
  pnl: number
  drawdown: number
}

interface CalendarDay {
  date: string
  pnl: number
  tradesCount: number
  winRate: number
  color: 'green' | 'red' | 'gray'
  intensity: number
}

interface AdvancedStatistics {
  sharpeRatio: number
  sortinoRatio: number
  calmarRatio: number
  maxDrawdown: number
  maxDrawdownAmount: number
  averageDrawdown: number
  recoveryFactor: number
  ulcerIndex: number
  zScore: number
  expectancyPerR: number
  systemQualityNumber: number
  ahpr: number
  ghpr: number
  standardDeviation: number
  downsideDeviation: number
  avgWinningStreak: number
  avgLosingStreak: number
  payoffRatio: number
  kellyPercentage: number
  avgHoldingTimeWins: number
  avgHoldingTimeLosses: number
  bestTradingHour: string
  worstTradingHour: string
  bestTradingDay: string
  worstTradingDay: string
  last7DaysPnL: number
  last30DaysPnL: number
  last7DaysWinRate: number
  last30DaysWinRate: number
}

interface WeeklyReport {
  period: string
  startDate: string
  endDate: string
  metrics: PerformanceMetrics
  advancedStats: AdvancedStatistics
  dailyPnL: Array<{ date: string; pnl: number; tradesCount: number }>
  topSymbols: Array<{ symbol: string; pnl: number; trades: number; winRate: number }>
  comparison: {
    pnlChange: number
    winRateChange: number
    tradesChange: number
  }
}

// ============================================================================
// AUTH HELPERS
// ============================================================================

const AUTH_KEY = 'kitbot_journal_user'

interface User {
  id: string
  email: string
  name: string
  avatar?: string
  createdAt: string
}

function getStoredUser(): User | null {
  try {
    const stored = localStorage.getItem(AUTH_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function storeUser(user: User) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(user))
}

function clearUser() {
  localStorage.removeItem(AUTH_KEY)
}

// ============================================================================
// GLASSMORPHISM COMPONENTS
// ============================================================================

const GlassCard = ({ children, className = '', glow = false }: { children: React.ReactNode, className?: string, glow?: boolean }) => (
  <div className={`
    relative backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl
    ${glow ? 'shadow-[0_0_50px_rgba(6,182,212,0.15)]' : 'shadow-xl'}
    transition-all duration-300 hover:bg-white/[0.07] hover:border-white/20
    ${className}
  `}>
    {children}
  </div>
)

const GradientText = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <span className={`bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent ${className}`}>
    {children}
  </span>
)

const MetricCard = ({ label, value, subValue, trend, icon }: { 
  label: string, value: string | number, subValue?: string, trend?: 'up' | 'down' | 'neutral', icon?: string 
}) => (
  <GlassCard className="p-3 sm:p-5 group">
    <div className="flex items-start justify-between gap-1">
      <div className="min-w-0 flex-1">
        <div className="text-gray-400 text-[10px] sm:text-xs uppercase tracking-wider mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2 truncate">
          {icon && <span className="text-sm sm:text-base flex-shrink-0">{icon}</span>}
          <span className="truncate">{label}</span>
        </div>
        <div className={`text-lg sm:text-3xl font-bold tracking-tight truncate ${
          trend === 'up' ? 'text-emerald-400' : 
          trend === 'down' ? 'text-rose-400' : 
          'text-white'
        }`}>
          {value}
        </div>
        {subValue && (
          <div className="text-gray-500 text-[10px] sm:text-sm mt-0.5 sm:mt-1 truncate">{subValue}</div>
        )}
      </div>
      {trend && (
        <div className={`p-1 sm:p-2 rounded-lg sm:rounded-xl flex-shrink-0 hidden sm:block ${
          trend === 'up' ? 'bg-emerald-500/20' : 
          trend === 'down' ? 'bg-rose-500/20' : 
          'bg-gray-500/20'
        }`}>
          {trend === 'up' ? '📈' : trend === 'down' ? '📉' : '➡️'}
        </div>
      )}
    </div>
  </GlassCard>
)

// ============================================================================
// SIDEBAR NAVIGATION
// ============================================================================

const Sidebar = ({ activeTab, setActiveTab, user, onLogout }: { 
  activeTab: string, 
  setActiveTab: (tab: string) => void,
  user: User | null,
  onLogout: () => void
}) => {
  const navItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'trades', icon: '📋', label: 'Trade Log' },
    { id: 'analytics', icon: '📈', label: 'Analytics' },
    { id: 'calendar', icon: '📅', label: 'Calendar' },
    { id: 'accounts', icon: '💼', label: 'Accounts' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ]

  return (
    <div className="fixed left-0 top-12 h-[calc(100%-48px)] w-20 lg:w-64 bg-black/40 backdrop-blur-2xl border-r border-white/10 flex flex-col z-50">
      {/* Journal Logo/Title */}
      <div className="p-3 lg:p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-lg lg:text-xl font-bold">
            📓
          </div>
          <span className="hidden lg:block text-lg font-bold">
            <GradientText>Trading</GradientText>
            <span className="text-white"> Journal</span>
          </span>
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 p-3 lg:p-4 space-y-2">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`
              w-full flex items-center gap-3 px-3 lg:px-4 py-3 rounded-xl transition-all duration-200
              ${activeTab === item.id 
                ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
              }
            `}
          >
            <span className="text-xl">{item.icon}</span>
            <span className="hidden lg:block font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* User Profile */}
      {user && (
        <div className="p-4 border-t border-white/10">
          <div className="flex flex-col lg:flex-row items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-lg">
              {(user.name || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="hidden lg:block flex-1 min-w-0">
              <div className="text-white font-medium truncate">{user.name}</div>
              <div className="text-gray-500 text-xs truncate">{user.email}</div>
            </div>
            <button 
              onClick={onLogout}
              className="text-gray-400 hover:text-rose-400 transition-colors p-2 lg:p-0"
              title="Logout"
            >
              🚪
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// EQUITY CHART (Area Fill)
// ============================================================================

const EquityChart = ({ data }: { data: EquityPoint[] }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    if (!chartContainerRef.current || data.length === 0) return
    
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#6b7280',
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 300,
      rightPriceScale: { 
        borderColor: 'rgba(255,255,255,0.1)',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: { 
        borderColor: 'rgba(255,255,255,0.1)',
        timeVisible: true,
      },
      crosshair: {
        vertLine: { color: 'rgba(6,182,212,0.3)', width: 1, style: 2 },
        horzLine: { color: 'rgba(6,182,212,0.3)', width: 1, style: 2 },
      },
    })
    
    const areaSeries = chart.addAreaSeries({
      topColor: 'rgba(6, 182, 212, 0.4)',
      bottomColor: 'rgba(6, 182, 212, 0.0)',
      lineColor: 'rgba(6, 182, 212, 1)',
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 6,
      crosshairMarkerBackgroundColor: '#06b6d4',
    })
    
    const chartData: AreaData[] = data.map(d => ({
      time: d.date as Time,
      value: d.balance
    }))
    
    areaSeries.setData(chartData)
    chart.timeScale().fitContent()
    
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth })
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
    }
  }, [data])
  
  if (data.length === 0) {
    return (
      <GlassCard className="p-6" glow>
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <span className="text-2xl">📈</span> Equity Curve
        </h3>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          Add trades to see your equity curve
        </div>
      </GlassCard>
    )
  }
  
  return (
    <GlassCard className="p-6" glow>
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
        <span className="text-2xl">📈</span> Equity Curve
      </h3>
      <div ref={chartContainerRef} />
    </GlassCard>
  )
}

// ============================================================================
// DRAWDOWN CHART (Underwater Equity)
// ============================================================================

const DrawdownChart = ({ data }: { data: EquityPoint[] }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    if (!chartContainerRef.current || data.length === 0) return
    
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#6b7280',
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 200,
      rightPriceScale: { 
        borderColor: 'rgba(255,255,255,0.1)',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: { 
        borderColor: 'rgba(255,255,255,0.1)',
        timeVisible: true,
      },
      crosshair: {
        vertLine: { color: 'rgba(244,63,94,0.3)', width: 1, style: 2 },
        horzLine: { color: 'rgba(244,63,94,0.3)', width: 1, style: 2 },
      },
    })
    
    // Create negative area series for drawdown (inverted, shown as red below zero)
    const areaSeries = chart.addAreaSeries({
      topColor: 'rgba(244, 63, 94, 0.0)',
      bottomColor: 'rgba(244, 63, 94, 0.4)',
      lineColor: 'rgba(244, 63, 94, 1)',
      lineWidth: 2,
      invertFilledArea: true,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: '#f43f5e',
    })
    
    // Convert drawdown to negative values (underwater)
    const chartData: AreaData[] = data.map(d => ({
      time: d.date as Time,
      value: -d.drawdown // Negative to show below zero line
    }))
    
    areaSeries.setData(chartData)
    chart.timeScale().fitContent()
    
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth })
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
    }
  }, [data])
  
  // Calculate max drawdown stats
  const maxDD = data.length > 0 ? Math.max(...data.map(d => d.drawdown)) : 0
  const currentDD = data.length > 0 ? data[data.length - 1]?.drawdown || 0 : 0
  const avgDD = data.length > 0 ? data.reduce((sum, d) => sum + d.drawdown, 0) / data.length : 0
  
  if (data.length === 0) {
    return (
      <GlassCard className="p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <span className="text-2xl">📉</span> Drawdown Chart
        </h3>
        <div className="h-[200px] flex items-center justify-center text-gray-500">
          Add trades to see your drawdown analysis
        </div>
      </GlassCard>
    )
  }
  
  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <span className="text-2xl">📉</span> Drawdown (Underwater Equity)
        </h3>
        <div className="flex gap-4 text-sm">
          <div className="text-center">
            <div className="text-gray-400 text-xs">Current</div>
            <div className={`font-bold ${currentDD > 5 ? 'text-rose-400' : currentDD > 2 ? 'text-yellow-400' : 'text-emerald-400'}`}>
              -{currentDD.toFixed(1)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-xs">Max</div>
            <div className="font-bold text-rose-400">-{maxDD.toFixed(1)}%</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-xs">Average</div>
            <div className="font-bold text-yellow-400">-{avgDD.toFixed(1)}%</div>
          </div>
        </div>
      </div>
      <div ref={chartContainerRef} />
    </GlassCard>
  )
}

// ============================================================================
// P&L CALENDAR HEATMAP
// ============================================================================

const PnLCalendar = ({ data, fullSize = false }: { data: CalendarDay[], fullSize?: boolean }) => {
  const getColor = (day: CalendarDay) => {
    if (day.tradesCount === 0) return 'bg-white/5'
    const intensity = Math.min(day.intensity * 1.5, 1)
    if (day.color === 'green') {
      if (intensity > 0.7) return 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]'
      if (intensity > 0.4) return 'bg-emerald-500/70'
      return 'bg-emerald-500/40'
    }
    if (day.color === 'red') {
      if (intensity > 0.7) return 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]'
      if (intensity > 0.4) return 'bg-rose-500/70'
      return 'bg-rose-500/40'
    }
    return 'bg-white/5'
  }

  // Group by weeks
  const weeks: CalendarDay[][] = []
  let currentWeek: CalendarDay[] = []
  data.forEach((day, i) => {
    currentWeek.push(day)
    if (currentWeek.length === 7 || i === data.length - 1) {
      weeks.push(currentWeek)
      currentWeek = []
    }
  })
  
  return (
    <GlassCard className="p-6">
      <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
        <span className="text-2xl">📅</span> P&L Calendar
      </h3>
      {data.length === 0 ? (
        <div className="text-gray-500 text-center py-8">No trading data yet</div>
      ) : (
        <>
          <div className="flex gap-1.5 flex-wrap justify-center">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-1.5">
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`
                      w-7 h-7 rounded-lg ${getColor(day)} cursor-pointer 
                      transition-all duration-200 hover:scale-110 hover:ring-2 hover:ring-cyan-400/50
                    `}
                    title={`${day.date}\nP&L: $${(day.pnl ?? 0).toFixed(2)}\nTrades: ${day.tradesCount ?? 0}\nWin Rate: ${(day.winRate ?? 0).toFixed(0)}%`}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-6 mt-6 text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-rose-500" /> Loss
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-white/10" /> No trades
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500" /> Profit
            </div>
          </div>
        </>
      )}
    </GlassCard>
  )
}

// ============================================================================
// TRADE FILTERS COMPONENT
// ============================================================================

interface TradeFilters {
  symbol: string
  direction: 'ALL' | 'LONG' | 'SHORT'
  setup: string
  emotion: string
  mistake: string
  outcome: 'ALL' | 'WIN' | 'LOSS'
  dateFrom: string
  dateTo: string
  minPnl: string
  maxPnl: string
  tag: string
}

const TradeFiltersPanel = ({ 
  filters, 
  setFilters, 
  trades,
  isOpen,
  setIsOpen 
}: { 
  filters: TradeFilters
  setFilters: (f: TradeFilters) => void
  trades: JournalEntry[]
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}) => {
  // Extract unique values from trades for dropdowns
  const uniqueSymbols = [...new Set(trades.map(t => t.symbol))].sort()
  const uniqueSetups = [...new Set(trades.map(t => t.setup).filter(Boolean))].sort()
  const uniqueTags = [...new Set(trades.flatMap(t => {
    if (!t.tags) return []
    try { return JSON.parse(t.tags) } catch { return t.tags.split(',').map(tag => tag.trim()) }
  }))].sort()

  const resetFilters = () => setFilters({
    symbol: '', direction: 'ALL', setup: '', emotion: '', mistake: '', outcome: 'ALL',
    dateFrom: '', dateTo: '', minPnl: '', maxPnl: '', tag: ''
  })

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => 
    v !== '' && v !== 'ALL'
  ).length

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
          activeFiltersCount > 0 
            ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
            : 'bg-white/5 text-gray-400 hover:bg-white/10'
        }`}
      >
        <span>🔍</span>
        <span>Filters</span>
        {activeFiltersCount > 0 && (
          <span className="px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full">
            {activeFiltersCount}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="p-4 bg-white/5 border border-white/10 rounded-xl mb-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-medium flex items-center gap-2">
          <span>🔍</span> Advanced Filters
          {activeFiltersCount > 0 && (
            <span className="px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full">
              {activeFiltersCount} active
            </span>
          )}
        </h4>
        <div className="flex gap-2">
          <button onClick={resetFilters} className="text-gray-400 hover:text-white text-sm">
            Reset
          </button>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white">
            ✕
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {/* Symbol */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Symbol</label>
          <select 
            value={filters.symbol} 
            onChange={e => setFilters({...filters, symbol: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All Symbols</option>
            {uniqueSymbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Direction */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Direction</label>
          <select 
            value={filters.direction} 
            onChange={e => setFilters({...filters, direction: e.target.value as any})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="ALL">All</option>
            <option value="LONG">↑ Long</option>
            <option value="SHORT">↓ Short</option>
          </select>
        </div>

        {/* Setup */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Setup</label>
          <select 
            value={filters.setup} 
            onChange={e => setFilters({...filters, setup: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All Setups</option>
            {uniqueSetups.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Emotion */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Emotion</label>
          <select 
            value={filters.emotion} 
            onChange={e => setFilters({...filters, emotion: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All</option>
            <option value="confident">😎 Confident</option>
            <option value="calm">😌 Calm</option>
            <option value="neutral">😐 Neutral</option>
            <option value="anxious">😰 Anxious</option>
            <option value="fearful">😨 Fearful</option>
            <option value="greedy">🤑 Greedy</option>
          </select>
        </div>

        {/* Mistake */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Mistake</label>
          <select 
            value={filters.mistake} 
            onChange={e => setFilters({...filters, mistake: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All</option>
            <option value="none">✅ No Mistakes</option>
            <option value="any">⚠️ Has Mistakes</option>
            <option value="fomo">😱 FOMO</option>
            <option value="revenge">😤 Revenge</option>
            <option value="oversize">📈 Oversize</option>
            <option value="no_stop">🚫 No Stop</option>
            <option value="moved_stop">↔️ Moved Stop</option>
            <option value="early_exit">⏱️ Early Exit</option>
            <option value="late_exit">🐌 Late Exit</option>
            <option value="chasing">🏃 Chasing</option>
            <option value="ignored_plan">📋 Ignored Plan</option>
            <option value="overtrading">🔄 Overtrading</option>
          </select>
        </div>

        {/* Outcome */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Outcome</label>
          <select 
            value={filters.outcome} 
            onChange={e => setFilters({...filters, outcome: e.target.value as any})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="ALL">All</option>
            <option value="WIN">✅ Winners</option>
            <option value="LOSS">❌ Losers</option>
          </select>
        </div>

        {/* Date From */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">From Date</label>
          <input 
            type="date" 
            value={filters.dateFrom}
            onChange={e => setFilters({...filters, dateFrom: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>

        {/* Date To */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">To Date</label>
          <input 
            type="date" 
            value={filters.dateTo}
            onChange={e => setFilters({...filters, dateTo: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>

        {/* Min P&L */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Min P&L ($)</label>
          <input 
            type="number" 
            value={filters.minPnl}
            onChange={e => setFilters({...filters, minPnl: e.target.value})}
            placeholder="-500"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>

        {/* Max P&L */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Max P&L ($)</label>
          <input 
            type="number" 
            value={filters.maxPnl}
            onChange={e => setFilters({...filters, maxPnl: e.target.value})}
            placeholder="1000"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>

        {/* Tag */}
        <div>
          <label className="block text-gray-500 text-xs mb-1">Tag</label>
          <select 
            value={filters.tag} 
            onChange={e => setFilters({...filters, tag: e.target.value})}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">All Tags</option>
            {uniqueTags.map(t => <option key={t} value={t}>#{t}</option>)}
          </select>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// TRADE LOG TABLE
// ============================================================================

const TradeLog = ({ trades, onAddTrade, showAll = false }: { trades: JournalEntry[], onAddTrade: () => void, showAll?: boolean }) => {
  const [filters, setFilters] = useState<TradeFilters>({
    symbol: '', direction: 'ALL', setup: '', emotion: '', mistake: '', outcome: 'ALL',
    dateFrom: '', dateTo: '', minPnl: '', maxPnl: '', tag: ''
  })
  const [showFilters, setShowFilters] = useState(false)

  const parseTags = (tags?: string) => {
    if (!tags) return []
    try { return JSON.parse(tags) } catch { return tags.split(',').map(t => t.trim()) }
  }

  // Apply filters
  const filteredTrades = trades.filter(trade => {
    if (filters.symbol && trade.symbol !== filters.symbol) return false
    if (filters.direction !== 'ALL' && trade.direction !== filters.direction) return false
    if (filters.setup && trade.setup !== filters.setup) return false
    if (filters.emotion && trade.emotion !== filters.emotion) return false
    // Mistake filter
    if (filters.mistake === 'none' && trade.mistake) return false
    if (filters.mistake === 'any' && !trade.mistake) return false
    if (filters.mistake && filters.mistake !== 'none' && filters.mistake !== 'any' && trade.mistake !== filters.mistake) return false
    if (filters.outcome === 'WIN' && (trade.pnl || 0) <= 0) return false
    if (filters.outcome === 'LOSS' && (trade.pnl || 0) >= 0) return false
    if (filters.dateFrom && trade.entryTime < filters.dateFrom) return false
    if (filters.dateTo && trade.entryTime > filters.dateTo + 'T23:59:59') return false
    if (filters.minPnl && (trade.pnl || 0) < parseFloat(filters.minPnl)) return false
    if (filters.maxPnl && (trade.pnl || 0) > parseFloat(filters.maxPnl)) return false
    if (filters.tag) {
      const tradeTags = parseTags(trade.tags)
      if (!tradeTags.includes(filters.tag)) return false
    }
    return true
  })

  // Calculate filtered stats
  const filteredStats = {
    count: filteredTrades.length,
    wins: filteredTrades.filter(t => (t.pnl || 0) > 0).length,
    losses: filteredTrades.filter(t => (t.pnl || 0) < 0).length,
    totalPnl: filteredTrades.reduce((sum, t) => sum + (t.pnl || 0), 0),
    winRate: filteredTrades.length > 0 
      ? (filteredTrades.filter(t => (t.pnl || 0) > 0).length / filteredTrades.length * 100) 
      : 0
  }

  const emotions: Record<string, { emoji: string, color: string }> = {
    confident: { emoji: '😎', color: 'text-emerald-400' },
    calm: { emoji: '😌', color: 'text-blue-400' },
    neutral: { emoji: '😐', color: 'text-gray-400' },
    anxious: { emoji: '😰', color: 'text-yellow-400' },
    fearful: { emoji: '😨', color: 'text-rose-400' },
    greedy: { emoji: '🤑', color: 'text-purple-400' },
  }

  return (
    <GlassCard className="overflow-hidden">
      <div className="p-6 border-b border-white/10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <span className="text-2xl">📋</span> {showAll ? 'All Trades' : 'Recent Trades'}
          </h3>
          <div className="flex items-center gap-3">
            {showAll && (
              <TradeFiltersPanel 
                filters={filters} 
                setFilters={setFilters} 
                trades={trades}
                isOpen={showFilters}
                setIsOpen={setShowFilters}
              />
            )}
            <button 
              onClick={onAddTrade}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium 
                         hover:from-cyan-400 hover:to-blue-500 transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)]"
            >
              + Add Trade
            </button>
          </div>
        </div>

        {/* Filtered Stats Bar */}
        {showAll && filteredTrades.length !== trades.length && (
          <div className="flex items-center gap-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-sm">
            <span className="text-cyan-400">📊 Filtered Results:</span>
            <span className="text-white">{filteredStats.count} trades</span>
            <span className="text-gray-400">|</span>
            <span className="text-emerald-400">{filteredStats.wins}W</span>
            <span className="text-rose-400">{filteredStats.losses}L</span>
            <span className="text-gray-400">|</span>
            <span className={(filteredStats.totalPnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
              ${(filteredStats.totalPnl ?? 0) >= 0 ? '+' : ''}{(filteredStats.totalPnl ?? 0).toFixed(2)}
            </span>
            <span className="text-gray-400">|</span>
            <span className={(filteredStats.winRate ?? 0) >= 50 ? 'text-emerald-400' : 'text-rose-400'}>
              {(filteredStats.winRate ?? 0).toFixed(1)}% WR
            </span>
          </div>
        )}
      </div>
      {filteredTrades.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-6xl mb-4">{trades.length === 0 ? '📝' : '🔍'}</div>
          <p className="text-gray-400 mb-6">
            {trades.length === 0 
              ? 'No trades recorded yet' 
              : 'No trades match your filters'}
          </p>
          {trades.length === 0 ? (
            <button 
              onClick={onAddTrade}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium
                         hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all duration-300"
            >
              Add Your First Trade
            </button>
          ) : (
            <button 
              onClick={() => setFilters({
                symbol: '', direction: 'ALL', setup: '', emotion: '', mistake: '', outcome: 'ALL',
                dateFrom: '', dateTo: '', minPnl: '', maxPnl: '', tag: ''
              })}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-gray-400 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-left">Symbol</th>
                <th className="px-6 py-4 text-left">Side</th>
                <th className="px-6 py-4 text-left">Setup</th>
                <th className="px-6 py-4 text-right">Entry</th>
                <th className="px-6 py-4 text-right">Exit</th>
                <th className="px-6 py-4 text-right">P&L</th>
                <th className="px-6 py-4 text-right">R</th>
                <th className="px-6 py-4 text-center">😊</th>
                <th className="px-6 py-4 text-center">⚠️</th>
                <th className="px-6 py-4 text-left">Tags</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map((trade, idx) => (
                <tr 
                  key={trade.id} 
                  className={`
                    border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer
                    ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}
                  `}
                >
                  <td className="px-6 py-4 text-gray-300">{trade.entryTime.split('T')[0]}</td>
                  <td className="px-6 py-4 text-white font-semibold">{trade.symbol}</td>
                  <td className="px-6 py-4">
                    <span className={`
                      px-3 py-1 rounded-lg text-xs font-bold
                      ${trade.direction === 'LONG' 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                      }
                    `}>
                      {trade.direction === 'LONG' ? '↑ LONG' : '↓ SHORT'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-400">{trade.setup || '-'}</td>
                  <td className="px-6 py-4 text-gray-300 text-right font-mono">{trade.entryPrice}</td>
                  <td className="px-6 py-4 text-gray-300 text-right font-mono">{trade.exitPrice || '-'}</td>
                  <td className={`px-6 py-4 text-right font-mono font-bold ${
                    (trade.pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {trade.pnl != null ? `${trade.pnl >= 0 ? '+' : ''}$${trade.pnl.toFixed(2)}` : '-'}
                  </td>
                  <td className={`px-6 py-4 text-right font-mono ${
                    (trade.rMultiple || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {trade.rMultiple != null ? `${trade.rMultiple >= 0 ? '+' : ''}${trade.rMultiple.toFixed(1)}R` : '-'}
                  </td>
                  <td className="px-6 py-4 text-center text-xl">
                    {trade.emotion && emotions[trade.emotion] ? (
                      <span title={trade.emotion}>{emotions[trade.emotion].emoji}</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-center text-xl">
                    {trade.mistake && TRADING_MISTAKES[trade.mistake as keyof typeof TRADING_MISTAKES] ? (
                      <span 
                        title={TRADING_MISTAKES[trade.mistake as keyof typeof TRADING_MISTAKES].label}
                        className="cursor-help"
                      >
                        {TRADING_MISTAKES[trade.mistake as keyof typeof TRADING_MISTAKES].emoji}
                      </span>
                    ) : (
                      <span className="text-emerald-400/50">✓</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {parseTags(trade.tags).slice(0, 2).map((tag: string) => (
                        <span key={tag} className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs border border-cyan-500/30">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  )
}

// ============================================================================
// LOGIN SCREEN
// ============================================================================

const GITHUB_CLIENT_ID = 'Ov23li7anBuOL7zvIJHq'
const GITHUB_REDIRECT_URI = 'https://kitbot.finance/kitview/auth/callback.html'

const LoginScreen = ({ onLogin }: { onLogin: (user: User) => void }) => {
  const [loading, setLoading] = useState(false)

  const handleGitHubLogin = () => {
    setLoading(true)
    const params = new URLSearchParams({
      client_id: GITHUB_CLIENT_ID,
      redirect_uri: GITHUB_REDIRECT_URI,
      scope: 'read:user user:email',
      state: Math.random().toString(36).substring(2, 15),
    })
    window.location.href = `https://github.com/login/oauth/authorize?${params.toString()}`
  }

  // Check for existing user on mount
  useEffect(() => {
    const existingUser = getStoredUser()
    if (existingUser) {
      onLogin(existingUser)
    }
  }, [onLogin])

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      <GlassCard className="w-full max-w-md p-8 relative" glow>
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-3xl font-bold mb-4">
            K
          </div>
          <h1 className="text-2xl font-bold">
            <GradientText>K.I.T.</GradientText>
            <span className="text-white"> Trading Journal</span>
          </h1>
          <p className="text-gray-400 mt-2">Track. Analyze. Improve.</p>
        </div>

        {/* Features */}
        <div className="space-y-3 mb-8">
          <div className="flex items-center gap-3 text-gray-300">
            <span className="text-cyan-400">✓</span>
            <span>Track all your trades in one place</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <span className="text-cyan-400">✓</span>
            <span>Connect MT4, MT5, Binance & more</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <span className="text-cyan-400">✓</span>
            <span>AI-powered trade analysis</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <span className="text-cyan-400">✓</span>
            <span>Sync across all devices</span>
          </div>
        </div>

        {/* GitHub Login Button */}
        <button
          onClick={handleGitHubLogin}
          disabled={loading}
          className="w-full py-4 bg-[#24292e] hover:bg-[#2f363d] text-white rounded-xl font-bold
                     flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-50
                     border border-gray-700 hover:border-gray-600"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
          </svg>
          {loading ? 'Redirecting...' : 'Continue with GitHub'}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-700"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#0a0a0f] text-gray-500">secure authentication</span>
          </div>
        </div>

        <p className="text-gray-500 text-xs text-center">
          🔒 Your data syncs across all devices with your GitHub account
        </p>
      </GlassCard>
    </div>
  )
}

// ============================================================================
// ADD TRADE MODAL
// ============================================================================

const AddTradeModal = ({ accountId, onClose, onSuccess }: { accountId: string, onClose: () => void, onSuccess: () => void }) => {
  const [formData, setFormData] = useState({
    symbol: '', direction: 'LONG' as 'LONG' | 'SHORT', entryPrice: '', exitPrice: '', quantity: '1',
    stopLoss: '', takeProfit: '', riskAmount: '', setup: '', emotion: '', mistake: '',
    entryTime: new Date().toISOString().slice(0, 16), exitTime: '', notes: '', tags: '',
  })

  const createTrade = useMutation({
    mutationFn: (data: any) => apiFetch('/api/journal/trades', {
      method: 'POST', body: JSON.stringify({ ...data, accountId }),
    }),
    onSuccess: () => { onSuccess(); onClose() },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createTrade.mutate({
      ...formData,
      entryPrice: parseFloat(formData.entryPrice),
      exitPrice: formData.exitPrice ? parseFloat(formData.exitPrice) : undefined,
      quantity: parseFloat(formData.quantity),
      stopLoss: formData.stopLoss ? parseFloat(formData.stopLoss) : undefined,
      takeProfit: formData.takeProfit ? parseFloat(formData.takeProfit) : undefined,
      riskAmount: formData.riskAmount ? parseFloat(formData.riskAmount) : undefined,
      exitTime: formData.exitTime || undefined,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : undefined,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <GlassCard className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" glow>
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Add New Trade</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Symbol *</label>
              <input type="text" required value={formData.symbol}
                onChange={e => setFormData({...formData, symbol: e.target.value.toUpperCase()})}
                placeholder="EURUSD" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Direction *</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setFormData({...formData, direction: 'LONG'})}
                  className={`flex-1 py-3 rounded-xl font-bold transition-all ${formData.direction === 'LONG' 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' 
                    : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                  ↑ LONG
                </button>
                <button type="button" onClick={() => setFormData({...formData, direction: 'SHORT'})}
                  className={`flex-1 py-3 rounded-xl font-bold transition-all ${formData.direction === 'SHORT' 
                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/50' 
                    : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                  ↓ SHORT
                </button>
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Entry Price *</label>
              <input type="number" step="any" required value={formData.entryPrice}
                onChange={e => setFormData({...formData, entryPrice: e.target.value})}
                placeholder="1.0850" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Exit Price</label>
              <input type="number" step="any" value={formData.exitPrice}
                onChange={e => setFormData({...formData, exitPrice: e.target.value})}
                placeholder="1.0900" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Lot Size *</label>
              <input type="number" step="any" required value={formData.quantity}
                onChange={e => setFormData({...formData, quantity: e.target.value})}
                placeholder="1.0" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Risk ($)</label>
              <input type="number" step="any" value={formData.riskAmount}
                onChange={e => setFormData({...formData, riskAmount: e.target.value})}
                placeholder="100" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Entry Time *</label>
              <input type="datetime-local" required value={formData.entryTime}
                onChange={e => setFormData({...formData, entryTime: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Exit Time</label>
              <input type="datetime-local" value={formData.exitTime}
                onChange={e => setFormData({...formData, exitTime: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Setup</label>
              <input type="text" value={formData.setup}
                onChange={e => setFormData({...formData, setup: e.target.value})}
                placeholder="Breakout, Pullback..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Emotion</label>
              <select value={formData.emotion} onChange={e => setFormData({...formData, emotion: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                <option value="">Select...</option>
                <option value="confident">😎 Confident</option>
                <option value="calm">😌 Calm</option>
                <option value="neutral">😐 Neutral</option>
                <option value="anxious">😰 Anxious</option>
                <option value="fearful">😨 Fearful</option>
                <option value="greedy">🤑 Greedy</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Mistake (if any)</label>
              <select value={formData.mistake} onChange={e => setFormData({...formData, mistake: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                <option value="">No mistake</option>
                <option value="fomo">😱 FOMO Entry</option>
                <option value="revenge">😤 Revenge Trade</option>
                <option value="oversize">📈 Oversized Position</option>
                <option value="no_stop">🚫 No Stop Loss</option>
                <option value="moved_stop">↔️ Moved Stop Loss</option>
                <option value="early_exit">⏱️ Exited Too Early</option>
                <option value="late_exit">🐌 Exited Too Late</option>
                <option value="chasing">🏃 Chased Price</option>
                <option value="ignored_plan">📋 Ignored Trading Plan</option>
                <option value="overtrading">🔄 Overtrading</option>
                <option value="news_trade">📰 Traded Into News</option>
                <option value="tired">😴 Traded While Tired</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Tags (comma separated)</label>
            <input type="text" value={formData.tags}
              onChange={e => setFormData({...formData, tags: e.target.value})}
              placeholder="trend, breakout, news" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Notes</label>
            <textarea value={formData.notes} rows={3}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              placeholder="Trade notes..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
          </div>
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all">
              Cancel
            </button>
            <button type="submit" disabled={createTrade.isPending}
              className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-bold
                         hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50">
              {createTrade.isPending ? 'Adding...' : 'Add Trade'}
            </button>
          </div>
        </form>
      </GlassCard>
    </div>
  )
}

// ============================================================================
// ADD ACCOUNT MODAL
// ============================================================================

// Platform configurations
const PLATFORMS = [
  { id: 'mt5', name: 'MetaTrader 5', icon: '📊', desc: 'RoboForex, IC Markets, XM' },
  { id: 'mt4', name: 'MetaTrader 4', icon: '📈', desc: 'Classic MT4 brokers' },
  { id: 'binance', name: 'Binance', icon: '🟡', desc: 'Spot & Futures' },
  { id: 'binaryfaster', name: 'BinaryFaster', icon: '⚡', desc: 'Binary Options' },
  { id: 'bybit', name: 'Bybit', icon: '🟠', desc: 'Derivatives & Spot' },
  { id: 'ctrader', name: 'cTrader', icon: '💜', desc: 'Pepperstone, IC Markets' },
  { id: 'tradingview', name: 'TradingView', icon: '📺', desc: 'Webhook alerts' },
  { id: 'ibkr', name: 'Interactive Brokers', icon: '🏛️', desc: 'Stocks, Options, Forex' },
  { id: 'tradelocker', name: 'TradeLocker', icon: '🔐', desc: 'Prop firm platform' },
  { id: 'manual', name: 'Manual Entry', icon: '✍️', desc: 'Log trades yourself' },
] as const

type PlatformId = typeof PLATFORMS[number]['id']

// Platform to default broker mapping
const PLATFORM_BROKERS: Record<PlatformId, string> = {
  mt5: 'RoboForex',
  mt4: 'RoboForex',
  binance: 'Binance',
  binaryfaster: 'BinaryFaster',
  bybit: 'Bybit',
  ctrader: 'Pepperstone',
  tradingview: 'TradingView',
  ibkr: 'Interactive Brokers',
  tradelocker: 'TradeLocker',
  manual: 'Manual',
}

const AddAccountModal = ({ onClose, onSuccess }: { onClose: () => void, onSuccess: () => void }) => {
  const [platform, setPlatform] = useState<PlatformId>('mt5')
  const [formData, setFormData] = useState({
    name: '', broker: 'RoboForex', accountType: 'live' as 'live' | 'demo' | 'prop_firm',
    currency: 'USD', initialBalance: '10000', propFirmName: '',
    // MT4/MT5 fields
    server: '', login: '', password: '',
    // Binance/Bybit fields
    apiKey: '', apiSecret: '',
    // BinaryFaster fields
    binaryEmail: '', binaryPassword: '',
    // cTrader fields
    ctid: '',
    // IBKR fields
    ibkrAccount: '', ibkrHost: 'localhost', ibkrPort: '7497',
    // TradeLocker fields
    tradelockerEmail: '',
  })

  // Auto-update broker when platform changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, broker: PLATFORM_BROKERS[platform] || platform }))
  }, [platform])

  // Get stored token for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('kitbot_token')
    return token ? { 'Authorization': `Bearer ${token}` } : {}
  }

  // Build credentials object based on platform
  const buildCredentials = () => {
    switch (platform) {
      case 'mt4':
      case 'mt5':
        return { server: formData.server, login: formData.login, password: formData.password }
      case 'binance':
      case 'bybit':
        return { apiKey: formData.apiKey, apiSecret: formData.apiSecret }
      case 'binaryfaster':
        return { email: formData.binaryEmail, password: formData.binaryPassword }
      case 'ctrader':
        return { ctid: formData.ctid, password: formData.password }
      case 'tradingview':
        return { username: formData.login }
      case 'ibkr':
        return { account: formData.ibkrAccount, host: formData.ibkrHost, port: formData.ibkrPort }
      case 'tradelocker':
        return { email: formData.tradelockerEmail, password: formData.password }
      default:
        return {}
    }
  }

  const saveConnection = useMutation({
    mutationFn: (data: any) => apiFetch('/api/connections', { 
      method: 'POST', 
      body: JSON.stringify(data),
      headers: getAuthHeaders(),
    }),
    onSuccess: () => { onSuccess(); onClose() },
    onError: (err) => {
      console.error('Failed to save connection:', err)
      // Still close modal, local account was created
      onSuccess(); onClose()
    },
  })

  const createAccount = useMutation({
    mutationFn: (data: any) => apiFetch('/api/user/journal/accounts', { method: 'POST', body: JSON.stringify(data), headers: getAuthHeaders() }),
    onSuccess: (result) => {
      if (platform !== 'manual') {
        // Save connection to Supabase via /api/connections
        saveConnection.mutate({
          platform: platform,
          name: formData.name,
          broker: formData.broker,
          account_type: formData.accountType,
          credentials: buildCredentials(),
        })
      } else {
        onSuccess()
        onClose()
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createAccount.mutate({ 
      ...formData, 
      initialBalance: parseFloat(formData.initialBalance),
      connectionType: platform === 'manual' ? 'manual' : 'auto',
      platform: platform,
    })
  }

  const isPending = createAccount.isPending || saveConnection.isPending

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <GlassCard className="w-full max-w-md" glow>
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Add Trading Account</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Platform Selector Grid */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {PLATFORMS.map((p) => (
              <button key={p.id} type="button"
                onClick={() => setPlatform(p.id)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  platform === p.id 
                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' 
                    : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>
                <div className="text-xl mb-1">{p.icon}</div>
                <div className="font-medium text-xs">{p.name}</div>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Account Name *</label>
            <input type="text" required value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="My RoboForex Account" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
          </div>

          {/* MT4/MT5 Fields */}
          {(platform === 'mt5' || platform === 'mt4') && (
            <>
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-sm text-cyan-300">
                💡 Trades sync automatically via K.I.T. bot running on your PC
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">{platform.toUpperCase()} Server *</label>
                <input type="text" required value={formData.server}
                  onChange={e => setFormData({...formData, server: e.target.value})}
                  placeholder="RoboForex-ECN" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Login *</label>
                  <input type="text" required value={formData.login}
                    onChange={e => setFormData({...formData, login: e.target.value})}
                    placeholder="12345678" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Password *</label>
                  <input type="password" required value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    placeholder="••••••••" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
              </div>
            </>
          )}

          {/* Binance/Bybit Fields */}
          {(platform === 'binance' || platform === 'bybit') && (
            <>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-sm text-yellow-300">
                🔑 Create API keys with <strong>read-only</strong> permissions for security
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">API Key *</label>
                <input type="text" required value={formData.apiKey}
                  onChange={e => setFormData({...formData, apiKey: e.target.value})}
                  placeholder="Enter your API key" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">API Secret *</label>
                <input type="password" required value={formData.apiSecret}
                  onChange={e => setFormData({...formData, apiSecret: e.target.value})}
                  placeholder="Enter your API secret" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm" />
              </div>
            </>
          )}

          {/* BinaryFaster Fields */}
          {platform === 'binaryfaster' && (
            <>
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl text-sm text-orange-300">
                ⚡ Binary Options Trading - Trades sync automatically from BinaryFaster
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email *</label>
                <input type="email" required value={formData.binaryEmail}
                  onChange={e => setFormData({...formData, binaryEmail: e.target.value})}
                  placeholder="your@email.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Password *</label>
                <input type="password" required value={formData.binaryPassword}
                  onChange={e => setFormData({...formData, binaryPassword: e.target.value})}
                  placeholder="••••••••" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
            </>
          )}

          {/* cTrader Fields */}
          {platform === 'ctrader' && (
            <>
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl text-sm text-purple-300">
                ⚡ Connect via cTrader Open API
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">cTrader ID (cTID) *</label>
                <input type="text" required value={formData.ctid}
                  onChange={e => setFormData({...formData, ctid: e.target.value})}
                  placeholder="Your cTID" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Password *</label>
                <input type="password" required value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
            </>
          )}

          {/* TradingView Fields */}
          {platform === 'tradingview' && (
            <>
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm text-blue-300">
                📺 Import trades via TradingView webhook alerts
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">TradingView Username *</label>
                <input type="text" required value={formData.login}
                  onChange={e => setFormData({...formData, login: e.target.value})}
                  placeholder="Your TradingView username" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
            </>
          )}

          {/* IBKR Fields */}
          {platform === 'ibkr' && (
            <>
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl text-sm text-green-300">
                🏛️ Connect via IB Gateway (must be running locally)
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Account ID *</label>
                <input type="text" required value={formData.ibkrAccount}
                  onChange={e => setFormData({...formData, ibkrAccount: e.target.value})}
                  placeholder="U1234567" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Gateway Host</label>
                  <input type="text" value={formData.ibkrHost}
                    onChange={e => setFormData({...formData, ibkrHost: e.target.value})}
                    placeholder="localhost" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Port</label>
                  <input type="text" value={formData.ibkrPort}
                    onChange={e => setFormData({...formData, ibkrPort: e.target.value})}
                    placeholder="7497" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
              </div>
            </>
          )}

          {/* TradeLocker Fields */}
          {platform === 'tradelocker' && (
            <>
              <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-sm text-rose-300">
                🔐 Connect to TradeLocker prop firm accounts
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email *</label>
                <input type="email" required value={formData.tradelockerEmail}
                  onChange={e => setFormData({...formData, tradelockerEmail: e.target.value})}
                  placeholder="your@email.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Password *</label>
                <input type="password" required value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
              </div>
            </>
          )}

          {/* Manual Entry Info + Broker Input */}
          {platform === 'manual' && (
            <>
              <div className="p-3 bg-gray-500/10 border border-gray-500/30 rounded-xl text-sm text-gray-300">
                ✍️ You'll manually log each trade - great for brokers without API access
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Broker</label>
                  <input type="text" value={formData.broker}
                    onChange={e => setFormData({...formData, broker: e.target.value})}
                    placeholder="Your broker name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Type</label>
                  <select value={formData.accountType} onChange={e => setFormData({...formData, accountType: e.target.value as any})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                    <option value="demo">Demo</option>
                    <option value="live">Live</option>
                    <option value="prop_firm">Prop Firm</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Broker dropdown - only for MT4/MT5/cTrader */}
          {(platform === 'mt4' || platform === 'mt5' || platform === 'ctrader') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Broker</label>
                <select value={formData.broker} onChange={e => setFormData({...formData, broker: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                  <option value="RoboForex">RoboForex</option>
                  <option value="IC Markets">IC Markets</option>
                  <option value="Pepperstone">Pepperstone</option>
                  <option value="XM">XM</option>
                  <option value="FTMO">FTMO</option>
                  <option value="FundedNext">FundedNext</option>
                  <option value="The5ers">The5%ers</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Type</label>
                <select value={formData.accountType} onChange={e => setFormData({...formData, accountType: e.target.value as any})}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                  <option value="demo">Demo</option>
                  <option value="live">Live</option>
                  <option value="prop_firm">Prop Firm</option>
                </select>
              </div>
            </div>
          )}

          {/* Account Type for other platforms */}
          {(platform === 'binance' || platform === 'bybit' || platform === 'tradelocker') && (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Account Type</label>
              <select value={formData.accountType} onChange={e => setFormData({...formData, accountType: e.target.value as any})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white">
                <option value="live">Live</option>
                <option value="demo">Testnet/Demo</option>
              </select>
            </div>
          )}

          {/* Initial Balance - only for manual entry */}
          {platform === 'manual' && (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Initial Balance *</label>
              <input type="number" required value={formData.initialBalance}
                onChange={e => setFormData({...formData, initialBalance: e.target.value})}
                placeholder="10000" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium">Cancel</button>
            <button type="submit" disabled={isPending}
              className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-bold
                         hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50">
              {isPending ? 'Creating...' : platform !== 'manual' ? 'Connect & Create' : 'Create'}
            </button>
          </div>
        </form>
      </GlassCard>
    </div>
  )
}

// ============================================================================
// PLATFORM CONNECT MODAL
// ============================================================================

const PlatformConnectModal = ({ platform, onClose, onSuccess }: { platform: string, onClose: () => void, onSuccess: (platform: string) => void }) => {
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const platformConfig: Record<string, { icon: string, fields: { name: string, key: string, type: string, placeholder: string }[], description: string }> = {
    'MetaTrader 5': {
      icon: '📊',
      description: 'Connect your MT5 account for automatic trade syncing',
      fields: [
        { name: 'Server', key: 'server', type: 'text', placeholder: 'RoboForex-ECN' },
        { name: 'Login', key: 'login', type: 'text', placeholder: '12345678' },
        { name: 'Password', key: 'password', type: 'password', placeholder: '••••••••' },
      ]
    },
    'MetaTrader 4': {
      icon: '📈',
      description: 'Connect your MT4 account for automatic trade syncing',
      fields: [
        { name: 'Server', key: 'server', type: 'text', placeholder: 'RoboForex-Pro' },
        { name: 'Login', key: 'login', type: 'text', placeholder: '12345678' },
        { name: 'Password', key: 'password', type: 'password', placeholder: '••••••••' },
      ]
    },
    'cTrader': {
      icon: '📉',
      description: 'Connect your cTrader account via cTrader Open API',
      fields: [
        { name: 'cTrader ID', key: 'ctraderId', type: 'text', placeholder: '123456' },
        { name: 'Access Token', key: 'accessToken', type: 'password', placeholder: 'Your cTrader API token' },
      ]
    },
    'TradingView': {
      icon: '📺',
      description: 'Receive alerts from TradingView via webhooks',
      fields: [
        { name: 'Webhook Secret', key: 'webhookSecret', type: 'text', placeholder: 'Auto-generated' },
      ]
    },
    'Binance': {
      icon: '🟡',
      description: 'Connect your Binance account via API',
      fields: [
        { name: 'API Key', key: 'apiKey', type: 'text', placeholder: 'Your Binance API Key' },
        { name: 'API Secret', key: 'apiSecret', type: 'password', placeholder: 'Your Binance API Secret' },
      ]
    },
    'BinaryFaster': {
      icon: '⚡',
      description: 'Connect your BinaryFaster binary options account',
      fields: [
        { name: 'Email', key: 'email', type: 'email', placeholder: 'your@email.com' },
        { name: 'Password', key: 'password', type: 'password', placeholder: 'Your BinaryFaster password' },
      ]
    },
    'Bybit': {
      icon: '🟠',
      description: 'Connect your Bybit account via API',
      fields: [
        { name: 'API Key', key: 'apiKey', type: 'text', placeholder: 'Your Bybit API Key' },
        { name: 'API Secret', key: 'apiSecret', type: 'password', placeholder: 'Your Bybit API Secret' },
      ]
    },
    'Interactive Brokers': {
      icon: '🏦',
      description: 'Connect your IBKR account via Client Portal API',
      fields: [
        { name: 'Account ID', key: 'accountId', type: 'text', placeholder: 'U1234567' },
        { name: 'Client Portal Token', key: 'token', type: 'password', placeholder: 'Your IBKR token' },
      ]
    },
    'TradeLocker': {
      icon: '🔐',
      description: 'Connect your TradeLocker account',
      fields: [
        { name: 'Account ID', key: 'accountId', type: 'text', placeholder: 'Your TradeLocker ID' },
        { name: 'API Key', key: 'apiKey', type: 'password', placeholder: 'Your TradeLocker API Key' },
      ]
    },
  }

  const config = platformConfig[platform] || { icon: '🔗', description: 'Connect platform', fields: [] }

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsConnecting(true)
    setError(null)

    try {
      // Save connection to backend
      const response = await apiFetch('/api/journal/connections', {
        method: 'POST',
        body: JSON.stringify({
          platform: platform.toLowerCase().replace(/ /g, '_'),
          credentials: formData,
        }),
      })

      if (response.error) {
        throw new Error(response.error)
      }

      setSuccess(true)
      setTimeout(() => {
        onSuccess(platform.toLowerCase().replace(/ /g, '_').replace('metatrader_', 'mt'))
        onClose()
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Connection failed. Please check your credentials.')
    } finally {
      setIsConnecting(false)
    }
  }

  // Generate webhook URL for TradingView
  useEffect(() => {
    if (platform === 'TradingView') {
      setFormData({ webhookSecret: `kit_${Math.random().toString(36).substring(2, 15)}` })
    }
  }, [platform])

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="text-2xl">{config.icon}</span> Connect {platform}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>
        
        {success ? (
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <div className="text-xl font-bold text-green-400 mb-2">Connected!</div>
            <div className="text-gray-400">{platform} has been connected successfully</div>
          </div>
        ) : (
          <form onSubmit={handleConnect} className="p-6 space-y-4">
            <p className="text-gray-400 text-sm mb-4">{config.description}</p>
            
            {platform === 'TradingView' && (
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl mb-4">
                <div className="text-purple-300 font-medium mb-2">📡 Your Webhook URL:</div>
                <code className="text-xs text-purple-200 break-all">
                  https://kitbot-api-40qp.onrender.com/api/webhooks/tradingview/{formData.webhookSecret}
                </code>
                <p className="text-purple-300/70 text-xs mt-2">
                  Add this URL to your TradingView alert webhook settings
                </p>
              </div>
            )}

            {config.fields.map(field => (
              <div key={field.key}>
                <label className="block text-gray-400 text-sm mb-2">{field.name}</label>
                <input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500"
                  required={field.key !== 'webhookSecret'}
                  readOnly={field.key === 'webhookSecret'}
                />
              </div>
            ))}

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
                ❌ {error}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isConnecting}
                className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-bold
                           hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50"
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>

            <p className="text-gray-500 text-xs text-center mt-4">
              🔒 Your credentials are encrypted and stored securely
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Journal() {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(getStoredUser)
  const BUILD_VERSION = '20260212_v2'
  console.log('K.I.T. Journal loaded, version:', BUILD_VERSION)
  const [activeTab, setActiveTab] = useState<string>('dashboard')
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)
  const [showAddTrade, setShowAddTrade] = useState(false)
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [connectPlatform, setConnectPlatform] = useState<string | null>(null)
  const [connections, setConnections] = useState<Record<string, boolean>>({
    mt5: false, mt4: false, ctrader: false, tradingview: false,
    binance: false, bybit: false, ibkr: false, tradelocker: false
  })

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('kitbot_token')
    return token ? { 'Authorization': `Bearer ${token}` } : {}
  }

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const { data: accountsData, isLoading } = useQuery({
    queryKey: ['journal-accounts', user?.id || 'none'],
    queryFn: () => apiFetch('/api/user/journal/accounts', { headers: getAuthHeaders() }),
    enabled: !!user,
  })

  // Transform snake_case API response to camelCase
  const accounts: TradingAccount[] = (accountsData?.accounts || []).map((acc: any) => ({
    id: acc.id,
    name: acc.name,
    broker: acc.broker,
    accountType: acc.account_type || acc.accountType || 'demo',
    currency: acc.currency || 'USD',
    initialBalance: acc.initial_balance ?? acc.initialBalance ?? 0,
    currentBalance: acc.current_balance ?? acc.currentBalance ?? 0,
    propFirmName: acc.prop_firm_name || acc.propFirmName,
    totalTrades: acc.total_trades ?? acc.totalTrades ?? 0,
    winningTrades: acc.winning_trades ?? acc.winningTrades ?? 0,
    losingTrades: acc.losing_trades ?? acc.losingTrades ?? 0,
    totalPnL: acc.total_pnl ?? acc.totalPnL ?? 0,
    winRate: acc.win_rate ?? acc.winRate ?? 0,
    profitFactor: acc.profit_factor ?? acc.profitFactor ?? 0,
  }))
  const activeAccount = accounts.find(a => a.id === activeAccountId) || accounts[0]

  useEffect(() => {
    if (accounts.length > 0 && !activeAccountId) setActiveAccountId(accounts[0].id)
  }, [accounts, activeAccountId])

  const { data: tradesData } = useQuery({
    queryKey: ['journal-trades', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/entries?account_id=${activeAccount?.id}&limit=50`, { headers: getAuthHeaders() }),
    enabled: !!user && !!activeAccount?.id,
  })
  // Transform snake_case entries to camelCase
  const trades: JournalEntry[] = (tradesData?.entries || []).map((e: any) => ({
    id: e.id,
    accountId: e.account_id || e.accountId,
    symbol: e.symbol,
    direction: e.direction,
    entryPrice: e.entry_price ?? e.entryPrice ?? 0,
    exitPrice: e.exit_price ?? e.exitPrice,
    quantity: e.quantity ?? 0,
    stopLoss: e.stop_loss ?? e.stopLoss,
    takeProfit: e.take_profit ?? e.takeProfit,
    riskAmount: e.risk_amount ?? e.riskAmount,
    rMultiple: e.r_multiple ?? e.rMultiple,
    setup: e.setup,
    emotion: e.emotion,
    mistake: e.mistake,
    entryTime: e.entry_time || e.entry_date || e.entryTime,
    exitTime: e.exit_time || e.exit_date || e.exitTime,
    notes: e.notes,
    pnl: e.pnl,
    pnlPercent: e.pnl_percent ?? e.pnlPercent,
    isWin: e.is_win ?? e.isWin,
    status: e.status || 'closed',
    tags: e.tags,
    screenshotUrl: e.screenshot_entry || e.screenshotUrl,
  }))

  const { data: metricsData } = useQuery({
    queryKey: ['journal-metrics', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/metrics?accountId=${activeAccount?.id}`, { headers: getAuthHeaders() }),
    enabled: !!user && !!activeAccount?.id,
  })
  const metrics: PerformanceMetrics | null = metricsData?.metrics || null

  // Fetch connections
  const { data: connectionsData } = useQuery({
    queryKey: ['journal-connections', user?.id],
    queryFn: () => apiFetch('/api/user/journal/connections', { headers: getAuthHeaders() }),
    enabled: !!user,
  })

  const { data: calendarData } = useQuery({
    queryKey: ['journal-calendar', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/calendar?year=${new Date().getFullYear()}${activeAccount?.id ? `&account_id=${activeAccount.id}` : ''}`, { headers: getAuthHeaders() }),
    enabled: !!user && !!activeAccount?.id,
  })
  
  // Build calendar from trades if API doesn't return dailyPnL
  const calendar: CalendarDay[] = useMemo(() => {
    // If API returns dailyPnL, use it
    if (calendarData?.dailyPnL) {
      return Object.entries(calendarData.dailyPnL).map(([date, pnl]) => ({ 
        date, 
        pnl: pnl as number, 
        tradesCount: 0, 
        winRate: 0, 
        color: (pnl as number) > 0 ? 'green' : (pnl as number) < 0 ? 'red' : 'gray' as const, 
        intensity: 0.5 
      }))
    }
    // Otherwise, build from trades data
    if (trades.length > 0) {
      const dailyData: Record<string, { pnl: number, trades: number, wins: number }> = {}
      trades.forEach(t => {
        // Handle both ISO datetime (2024-02-13T10:30:00) and date-only (2024-02-13) formats
        const rawDate = t.entryTime || ''
        const date = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate.split(' ')[0]
        if (!date || date.length < 10) return
        if (!dailyData[date]) dailyData[date] = { pnl: 0, trades: 0, wins: 0 }
        dailyData[date].pnl += t.pnl || 0
        dailyData[date].trades += 1
        if (t.isWin) dailyData[date].wins += 1
      })
      return Object.entries(dailyData).map(([date, data]) => ({
        date,
        pnl: data.pnl,
        tradesCount: data.trades,
        winRate: data.trades > 0 ? (data.wins / data.trades) * 100 : 0,
        color: data.pnl > 0 ? 'green' : data.pnl < 0 ? 'red' : 'gray' as const,
        intensity: Math.min(Math.abs(data.pnl) / 100, 1)
      })).sort((a, b) => a.date.localeCompare(b.date))
    }
    return []
  }, [calendarData, trades])
  
  // Fetch equity curve
  const { data: equityData } = useQuery({
    queryKey: ['journal-equity', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/equity${activeAccount?.id ? `?accountId=${activeAccount.id}` : ''}`, { headers: getAuthHeaders() }),
    enabled: !!user,
  })
  const equityCurve: EquityPoint[] = equityData?.curve || []

  // Fetch advanced statistics (Sharpe, Sortino, Drawdown, etc.)
  const { data: advancedStatsData } = useQuery({
    queryKey: ['journal-advanced-stats', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/advanced-stats${activeAccount?.id ? `?accountId=${activeAccount.id}` : ''}`, { headers: getAuthHeaders() }),
    enabled: !!user,
  })
  const advancedStats: AdvancedStatistics | null = advancedStatsData?.stats || null

  // Fetch weekly report
  const { data: weeklyReportData } = useQuery({
    queryKey: ['journal-weekly-report', activeAccount?.id],
    queryFn: () => apiFetch(`/api/user/journal/report/weekly${activeAccount?.id ? `?accountId=${activeAccount.id}` : ''}`, { headers: getAuthHeaders() }),
    enabled: !!user,
  })
  const weeklyReport: WeeklyReport | null = weeklyReportData?.report || null

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['journal-accounts'] })
    queryClient.invalidateQueries({ queryKey: ['journal-trades'] })
    queryClient.invalidateQueries({ queryKey: ['journal-metrics'] })
    queryClient.invalidateQueries({ queryKey: ['journal-equity'] })
    queryClient.invalidateQueries({ queryKey: ['journal-calendar'] })
    queryClient.invalidateQueries({ queryKey: ['journal-advanced-stats'] })
    queryClient.invalidateQueries({ queryKey: ['journal-weekly-report'] })
  }

  // CSV Export handler
  const handleExportCSV = async () => {
    try {
      const response = await fetch(`https://kitbot-api-40qp.onrender.com/api/user/journal/export/csv${activeAccount?.id ? `?accountId=${activeAccount.id}` : ''}`, {
        headers: getAuthHeaders(),
      })
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `trades_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      alert('Failed to export CSV')
    }
  }

  const handleLogout = () => {
    clearUser()
    setUser(null)
  }

  const handleDeleteAccount = async (accountId: string, accountName: string) => {
    if (!confirm(`Are you sure you want to delete "${accountName}"? This will also delete all trades in this account.`)) {
      return
    }
    try {
      const result = await journalApiFetch(`/api/user/journal/accounts/${accountId}`, {
        method: 'DELETE'
      })
      if (!result.success) throw new Error(result.error || 'Failed to delete account')
      queryClient.invalidateQueries({ queryKey: ['journal-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] })
      if (activeAccountId === accountId) {
        setActiveAccountId(accounts.find(a => a.id !== accountId)?.id || '')
      }
    } catch (err) {
      console.error('Delete account error:', err)
      alert('Failed to delete account')
    }
  }

  // NOW we can do conditional returns after all hooks
  if (!user) {
    return <LoginScreen onLogin={setUser} />
  }

  const today = new Date().toISOString().split('T')[0]
  const todayTrades = trades.filter(t => t.entryTime.startsWith(today))
  const todayPnL = todayTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-cyan-400 text-xl animate-pulse">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-gray-900/95 backdrop-blur border-b border-gray-800 px-4 py-2 flex items-center justify-between text-sm z-[60]">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-bold">
            <span className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-600 rounded flex items-center justify-center text-white text-xs">K</span>
            <span className="hidden sm:block">K.I.T.</span>
          </Link>
          <div className="flex items-center gap-1 text-gray-400">
            <Link to="/forum" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white text-xs sm:text-sm">💬 <span className="hidden sm:inline">Forum</span></Link>
            <Link to="/terminal" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white text-xs sm:text-sm">🖥️ <span className="hidden sm:inline">Terminal</span></Link>
            <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs sm:text-sm">📓 <span className="hidden sm:inline">Journal</span></span>
            <Link to="/leaderboard" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white text-xs sm:text-sm hidden sm:block">🏆 Leaderboard</Link>
          </div>
        </div>
        <a href="https://kithub.finance" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-xs sm:text-sm">📦 <span className="hidden sm:inline">KitHub</span></a>
      </nav>

      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none pt-12">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Sidebar - adjusted for top nav */}
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="ml-20 lg:ml-64 pt-14 sm:pt-16 p-3 sm:p-6 lg:p-8 relative min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold text-white mb-1 truncate">
              {activeTab === 'dashboard' && 'Dashboard'}
              {activeTab === 'trades' && 'Trade Log'}
              {activeTab === 'analytics' && 'Analytics'}
              {activeTab === 'calendar' && 'Calendar'}
              {activeTab === 'accounts' && 'Accounts'}
              {activeTab === 'settings' && 'Settings'}
            </h1>
            <p className="text-gray-400 text-sm sm:text-base truncate">Welcome back, {user.name} 👋</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            {accounts.length > 0 && (
              <select value={activeAccount?.id || ''} onChange={(e) => setActiveAccountId(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs sm:text-sm min-w-0 max-w-[140px] sm:max-w-none truncate">
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name} (${(acc.currentBalance ?? 0).toLocaleString()})</option>
                ))}
              </select>
            )}
            <button onClick={() => setShowAddAccount(true)}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs sm:text-sm transition-all whitespace-nowrap">
              + Account
            </button>
            <button onClick={() => setShowAddTrade(true)} disabled={!activeAccount}
              className="px-3 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium text-xs sm:text-sm
                         hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 whitespace-nowrap">
              + Trade
            </button>
          </div>
        </div>

        {accounts.length === 0 ? (
          <GlassCard className="p-6 sm:p-16 text-center" glow>
            <div className="text-5xl sm:text-7xl mb-4 sm:mb-6">📊</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Welcome to Your Trading Journal</h2>
            <p className="text-gray-400 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Start tracking your trades and improve your performance with detailed analytics.
            </p>
            <button onClick={() => setShowAddAccount(true)}
              className="px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-bold text-base sm:text-lg
                         hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300">
              Create Your First Account
            </button>
          </GlassCard>
        ) : (
          <>
            {/* DASHBOARD TAB */}
            {activeTab === 'dashboard' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                  <MetricCard icon="💰" label="Today's P&L" value={`$${todayPnL >= 0 ? '+' : ''}${todayPnL.toFixed(0)}`}
                    subValue={`${todayTrades.length} trades`} trend={todayPnL >= 0 ? 'up' : 'down'} />
                  <MetricCard icon="📊" label="Total P&L" value={`$${(metrics?.totalPnL || 0) >= 0 ? '+' : ''}${(metrics?.totalPnL || 0).toFixed(0)}`}
                    subValue={`${metrics?.totalTrades || 0} trades`} trend={(metrics?.totalPnL || 0) >= 0 ? 'up' : 'down'} />
                  <MetricCard icon="🎯" label="Win Rate" value={`${(metrics?.winRate || 0).toFixed(1)}%`}
                    subValue={`${metrics?.winningTrades || 0}W / ${metrics?.losingTrades || 0}L`} trend={(metrics?.winRate || 0) >= 50 ? 'up' : 'down'} />
                  <MetricCard icon="⚡" label="Profit Factor" value={(metrics?.profitFactor || 0).toFixed(2)}
                    subValue="Risk reward" trend={(metrics?.profitFactor || 0) >= 1.5 ? 'up' : 'neutral'} />
                  <MetricCard icon="💎" label="Expectancy" value={`$${(metrics?.expectancy || 0).toFixed(0)}`}
                    subValue="Per trade" trend={(metrics?.expectancy || 0) > 0 ? 'up' : 'down'} />
                  <MetricCard icon="🔥" label="Streak" value={`${metrics?.currentStreak || 0}`}
                    subValue={metrics?.currentStreakType || 'none'} trend={metrics?.currentStreakType === 'win' ? 'up' : 'down'} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2"><EquityChart data={equityCurve} /></div>
                  <PnLCalendar data={calendar} />
                </div>
                <TradeLog trades={trades.slice(0, 10)} onAddTrade={() => setShowAddTrade(true)} />
              </div>
            )}

            {/* TRADE LOG TAB */}
            {activeTab === 'trades' && (
              <div className="space-y-6">
                <TradeLog trades={trades} onAddTrade={() => setShowAddTrade(true)} showAll />
              </div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Export Button */}
                <div className="flex justify-end">
                  <button onClick={handleExportCSV}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm transition-all flex items-center gap-2">
                    📥 Export CSV
                  </button>
                </div>

                {/* Basic Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard icon="📈" label="Gross Profit" value={`$${(metrics?.grossProfit || 0).toFixed(0)}`} trend="up" />
                  <MetricCard icon="📉" label="Gross Loss" value={`$${(metrics?.grossLoss || 0).toFixed(0)}`} trend="down" />
                  <MetricCard icon="🏆" label="Largest Win" value={`$${(metrics?.largestWin || 0).toFixed(0)}`} trend="up" />
                  <MetricCard icon="💀" label="Largest Loss" value={`$${(metrics?.largestLoss || 0).toFixed(0)}`} trend="down" />
                  <MetricCard icon="📊" label="Avg Win" value={`$${(metrics?.averageWin || 0).toFixed(0)}`} trend="up" />
                  <MetricCard icon="📉" label="Avg Loss" value={`$${(metrics?.averageLoss || 0).toFixed(0)}`} trend="down" />
                  <MetricCard icon="🔥" label="Win Streak" value={metrics?.longestWinStreak || 0} trend="up" />
                  <MetricCard icon="❄️" label="Lose Streak" value={metrics?.longestLoseStreak || 0} trend="down" />
                </div>

                {/* Advanced Statistics Panel (Myfxbook-style) */}
                <GlassCard className="p-6" glow>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">📊</span> Advanced Statistics
                    <span className="text-xs font-normal text-gray-400 ml-2">— Professional metrics like Myfxbook</span>
                  </h3>
                  
                  {advancedStats ? (
                    <div className="space-y-6">
                      {/* Risk-Adjusted Returns */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Risk-Adjusted Returns</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Sharpe Ratio</div>
                            <div className={`text-2xl font-bold ${advancedStats.sharpeRatio >= 1 ? 'text-emerald-400' : advancedStats.sharpeRatio >= 0 ? 'text-yellow-400' : 'text-rose-400'}`}>
                              {advancedStats.sharpeRatio.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">{advancedStats.sharpeRatio >= 2 ? 'Excellent' : advancedStats.sharpeRatio >= 1 ? 'Good' : advancedStats.sharpeRatio >= 0 ? 'Fair' : 'Poor'}</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Sortino Ratio</div>
                            <div className={`text-2xl font-bold ${advancedStats.sortinoRatio >= 2 ? 'text-emerald-400' : advancedStats.sortinoRatio >= 1 ? 'text-yellow-400' : 'text-rose-400'}`}>
                              {advancedStats.sortinoRatio.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">Downside risk adj.</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Calmar Ratio</div>
                            <div className={`text-2xl font-bold ${advancedStats.calmarRatio >= 1 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                              {advancedStats.calmarRatio.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">Return vs Drawdown</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Payoff Ratio</div>
                            <div className={`text-2xl font-bold ${advancedStats.payoffRatio >= 1.5 ? 'text-emerald-400' : advancedStats.payoffRatio >= 1 ? 'text-yellow-400' : 'text-rose-400'}`}>
                              {advancedStats.payoffRatio.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">Avg Win / Avg Loss</div>
                          </div>
                        </div>
                      </div>

                      {/* Drawdown Analysis */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Drawdown Analysis</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Max Drawdown</div>
                            <div className="text-2xl font-bold text-rose-400">{advancedStats.maxDrawdown.toFixed(1)}%</div>
                            <div className="text-gray-500 text-xs mt-1">${advancedStats.maxDrawdownAmount.toFixed(0)}</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Avg Drawdown</div>
                            <div className="text-2xl font-bold text-yellow-400">{advancedStats.averageDrawdown.toFixed(1)}%</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Recovery Factor</div>
                            <div className={`text-2xl font-bold ${advancedStats.recoveryFactor >= 2 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                              {advancedStats.recoveryFactor.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">Profit / Max DD</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Ulcer Index</div>
                            <div className={`text-2xl font-bold ${advancedStats.ulcerIndex < 5 ? 'text-emerald-400' : advancedStats.ulcerIndex < 10 ? 'text-yellow-400' : 'text-rose-400'}`}>
                              {advancedStats.ulcerIndex.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">DD severity</div>
                          </div>
                        </div>
                      </div>

                      {/* Statistical Edge */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Statistical Edge</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Z-Score</div>
                            <div className={`text-2xl font-bold ${Math.abs(advancedStats.zScore) > 1.96 ? 'text-purple-400' : 'text-gray-400'}`}>
                              {advancedStats.zScore.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">{Math.abs(advancedStats.zScore) > 1.96 ? 'Streaks detected!' : 'Random pattern'}</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">SQN (Van Tharp)</div>
                            <div className={`text-2xl font-bold ${advancedStats.systemQualityNumber >= 2 ? 'text-emerald-400' : advancedStats.systemQualityNumber >= 1.5 ? 'text-yellow-400' : 'text-gray-400'}`}>
                              {advancedStats.systemQualityNumber.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">{advancedStats.systemQualityNumber >= 2.5 ? 'Excellent' : advancedStats.systemQualityNumber >= 2 ? 'Good' : advancedStats.systemQualityNumber >= 1.5 ? 'Average' : 'Poor'}</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Expectancy/R</div>
                            <div className={`text-2xl font-bold ${advancedStats.expectancyPerR > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {advancedStats.expectancyPerR > 0 ? '+' : ''}{advancedStats.expectancyPerR.toFixed(2)}R
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Kelly %</div>
                            <div className={`text-2xl font-bold ${advancedStats.kellyPercentage > 0 ? 'text-cyan-400' : 'text-rose-400'}`}>
                              {advancedStats.kellyPercentage.toFixed(1)}%
                            </div>
                            <div className="text-gray-500 text-xs mt-1">Optimal size</div>
                          </div>
                        </div>
                      </div>

                      {/* Best/Worst Times */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Trading Patterns</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                            <div className="text-emerald-400 text-xs mb-1">🏆 Best Hour</div>
                            <div className="text-xl font-bold text-emerald-400">{advancedStats.bestTradingHour || 'N/A'}</div>
                          </div>
                          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl">
                            <div className="text-rose-400 text-xs mb-1">💀 Worst Hour</div>
                            <div className="text-xl font-bold text-rose-400">{advancedStats.worstTradingHour || 'N/A'}</div>
                          </div>
                          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                            <div className="text-emerald-400 text-xs mb-1">🏆 Best Day</div>
                            <div className="text-xl font-bold text-emerald-400">{advancedStats.bestTradingDay || 'N/A'}</div>
                          </div>
                          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl">
                            <div className="text-rose-400 text-xs mb-1">💀 Worst Day</div>
                            <div className="text-xl font-bold text-rose-400">{advancedStats.worstTradingDay || 'N/A'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Recent Performance */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Recent Performance</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Last 7 Days P&L</div>
                            <div className={`text-2xl font-bold ${advancedStats.last7DaysPnL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ${advancedStats.last7DaysPnL >= 0 ? '+' : ''}{advancedStats.last7DaysPnL.toFixed(0)}
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Last 7 Days WR</div>
                            <div className={`text-2xl font-bold ${advancedStats.last7DaysWinRate >= 50 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {advancedStats.last7DaysWinRate.toFixed(1)}%
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Last 30 Days P&L</div>
                            <div className={`text-2xl font-bold ${advancedStats.last30DaysPnL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ${advancedStats.last30DaysPnL >= 0 ? '+' : ''}{advancedStats.last30DaysPnL.toFixed(0)}
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Last 30 Days WR</div>
                            <div className={`text-2xl font-bold ${advancedStats.last30DaysWinRate >= 50 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {advancedStats.last30DaysWinRate.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      Add more trades to see advanced statistics (minimum 2 closed trades required)
                    </div>
                  )}
                </GlassCard>

                {/* Mistake Analytics Panel */}
                <GlassCard className="p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">⚠️</span> Mistake Analysis
                    <span className="text-xs font-normal text-gray-400 ml-2">— Identify patterns costing you money</span>
                  </h3>
                  {(() => {
                    const tradesWithMistakes = trades.filter(t => t.mistake)
                    const mistakeCounts: Record<string, { count: number, pnl: number }> = {}
                    
                    tradesWithMistakes.forEach(t => {
                      if (!t.mistake) return
                      if (!mistakeCounts[t.mistake]) mistakeCounts[t.mistake] = { count: 0, pnl: 0 }
                      mistakeCounts[t.mistake].count++
                      mistakeCounts[t.mistake].pnl += t.pnl || 0
                    })
                    
                    const sortedMistakes = Object.entries(mistakeCounts)
                      .sort((a, b) => a[1].pnl - b[1].pnl) // Sort by most costly
                    
                    const totalMistakePnl = tradesWithMistakes.reduce((sum, t) => sum + (t.pnl || 0), 0)
                    const cleanTrades = trades.filter(t => !t.mistake)
                    const cleanPnl = cleanTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
                    
                    if (trades.length === 0) {
                      return (
                        <div className="text-center text-gray-500 py-8">
                          No trades recorded yet to analyze mistakes
                        </div>
                      )
                    }
                    
                    return (
                      <div className="space-y-4">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Clean Trades</div>
                            <div className="text-emerald-400 text-2xl font-bold">{cleanTrades.length}</div>
                            <div className="text-emerald-400/70 text-sm">
                              ${cleanPnl >= 0 ? '+' : ''}{cleanPnl.toFixed(0)}
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Trades with Mistakes</div>
                            <div className="text-rose-400 text-2xl font-bold">{tradesWithMistakes.length}</div>
                            <div className="text-rose-400/70 text-sm">
                              ${totalMistakePnl >= 0 ? '+' : ''}{totalMistakePnl.toFixed(0)}
                            </div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Mistake Rate</div>
                            <div className={`text-2xl font-bold ${
                              trades.length > 0 && (tradesWithMistakes.length / trades.length) < 0.2 
                                ? 'text-emerald-400' : 'text-yellow-400'
                            }`}>
                              {trades.length > 0 ? ((tradesWithMistakes.length / trades.length) * 100).toFixed(0) : 0}%
                            </div>
                            <div className="text-gray-500 text-sm">of all trades</div>
                          </div>
                          <div className="p-4 bg-white/5 rounded-xl">
                            <div className="text-gray-400 text-xs mb-1">Cost of Mistakes</div>
                            <div className={`text-2xl font-bold ${totalMistakePnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ${Math.abs(totalMistakePnl).toFixed(0)}
                            </div>
                            <div className="text-gray-500 text-sm">
                              {totalMistakePnl < 0 ? 'lost' : 'despite mistakes'}
                            </div>
                          </div>
                        </div>

                        {/* Mistake Breakdown */}
                        {sortedMistakes.length > 0 ? (
                          <div className="space-y-2">
                            <div className="text-gray-400 text-sm font-medium mb-2">Most Costly Mistakes</div>
                            {sortedMistakes.map(([mistake, stats]) => {
                              const mistakeInfo = TRADING_MISTAKES[mistake as keyof typeof TRADING_MISTAKES]
                              return (
                                <div key={mistake} className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                                  <div className="flex items-center gap-3">
                                    <span className="text-2xl">{mistakeInfo?.emoji || '❓'}</span>
                                    <div>
                                      <div className="text-white font-medium">{mistakeInfo?.label || mistake}</div>
                                      <div className="text-gray-500 text-sm">{stats.count} trade{stats.count !== 1 ? 's' : ''}</div>
                                    </div>
                                  </div>
                                  <div className={`text-lg font-bold ${stats.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    ${stats.pnl >= 0 ? '+' : ''}{stats.pnl.toFixed(0)}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <div className="text-4xl mb-2">🎯</div>
                            <div className="text-emerald-400 font-medium">Great job! No mistakes recorded</div>
                            <div className="text-gray-500 text-sm">Keep tracking your trades to identify patterns</div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </GlassCard>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <EquityChart data={equityCurve} />
                  <DrawdownChart data={equityCurve} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <GlassCard className="p-6">
                    <h3 className="text-lg font-bold text-white mb-4">📅 P&L by Day</h3>
                    <PnLCalendar data={calendar} />
                  </GlassCard>
                  
                  {/* Weekly Report Summary */}
                  {weeklyReport && (
                    <GlassCard className="p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        📊 Weekly Performance
                        <span className="text-xs font-normal text-gray-400">
                          {weeklyReport.startDate} - {weeklyReport.endDate}
                        </span>
                      </h3>
                      <div className="space-y-4">
                        {/* Comparison to Last Week */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-3 bg-white/5 rounded-xl text-center">
                            <div className="text-gray-400 text-xs mb-1">P&L Change</div>
                            <div className={`text-lg font-bold ${weeklyReport.comparison.pnlChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {weeklyReport.comparison.pnlChange >= 0 ? '+' : ''}{weeklyReport.comparison.pnlChange.toFixed(1)}%
                            </div>
                            <div className="text-gray-500 text-xs">vs last week</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-xl text-center">
                            <div className="text-gray-400 text-xs mb-1">Win Rate Δ</div>
                            <div className={`text-lg font-bold ${weeklyReport.comparison.winRateChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {weeklyReport.comparison.winRateChange >= 0 ? '+' : ''}{weeklyReport.comparison.winRateChange.toFixed(1)}%
                            </div>
                            <div className="text-gray-500 text-xs">vs last week</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-xl text-center">
                            <div className="text-gray-400 text-xs mb-1">Trades Δ</div>
                            <div className={`text-lg font-bold ${weeklyReport.comparison.tradesChange >= 0 ? 'text-cyan-400' : 'text-gray-400'}`}>
                              {weeklyReport.comparison.tradesChange >= 0 ? '+' : ''}{weeklyReport.comparison.tradesChange}
                            </div>
                            <div className="text-gray-500 text-xs">vs last week</div>
                          </div>
                        </div>
                        
                        {/* Top Symbols */}
                        {weeklyReport.topSymbols.length > 0 && (
                          <div>
                            <div className="text-gray-400 text-xs mb-2">Top Symbols This Week</div>
                            <div className="space-y-2">
                              {weeklyReport.topSymbols.slice(0, 3).map((s, i) => (
                                <div key={s.symbol} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                                    <span className="text-white font-medium">{s.symbol}</span>
                                    <span className="text-gray-500 text-xs">{s.trades} trades</span>
                                  </div>
                                  <div className={`font-bold ${s.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    ${s.pnl >= 0 ? '+' : ''}{s.pnl.toFixed(0)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </GlassCard>
                  )}
                </div>
              </div>
            )}

            {/* CALENDAR TAB */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-white mb-6">📅 Trading Calendar</h3>
                  <PnLCalendar data={calendar} fullSize />
                </GlassCard>
              </div>
            )}

            {/* ACCOUNTS TAB */}
            {activeTab === 'accounts' && (
              <div className="space-y-6">
                {/* Total Portfolio Value */}
                <GlassCard className="p-6 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 border-cyan-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">💰 Total Portfolio Value</div>
                      <div className="text-4xl font-bold text-white">
                        ${accounts.reduce((sum, acc) => sum + (acc.currentBalance ?? 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-400 text-sm mb-1">Total P&L</div>
                      <div className={`text-2xl font-bold ${accounts.reduce((sum, acc) => sum + (acc.totalPnL ?? 0), 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {accounts.reduce((sum, acc) => sum + (acc.totalPnL ?? 0), 0) >= 0 ? '+' : ''}${accounts.reduce((sum, acc) => sum + (acc.totalPnL ?? 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                    </div>
                  </div>
                </GlassCard>
                
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white">Your Trading Accounts</h3>
                  <button onClick={() => setShowAddAccount(true)}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl">
                    + Add Account
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {accounts.map(acc => (
                    <GlassCard key={acc.id} className={`p-6 cursor-pointer ${activeAccount?.id === acc.id ? 'border-cyan-500' : ''}`}
                      onClick={() => setActiveAccountId(acc.id)}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-white">{acc.name}</h4>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${acc.accountType === 'live' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {acc.accountType}
                          </span>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteAccount(acc.id, acc.name); }}
                            className="p-1 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
                            title="Delete account">
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-white mb-2">${(acc.currentBalance ?? 0).toLocaleString()}</div>
                      <div className="text-sm text-gray-400">{acc.broker || 'No broker'}</div>
                      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
                        <div><div className="text-lg font-bold text-white">{acc.totalTrades ?? 0}</div><div className="text-xs text-gray-400">Trades</div></div>
                        <div><div className="text-lg font-bold text-green-400">{(acc.winRate ?? 0).toFixed(0)}%</div><div className="text-xs text-gray-400">Win Rate</div></div>
                        <div><div className={`text-lg font-bold ${(acc.totalPnL ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>${(acc.totalPnL ?? 0).toFixed(0)}</div><div className="text-xs text-gray-400">P&L</div></div>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-white mb-6">⚙️ Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div><div className="text-white font-medium">Display Currency</div><div className="text-gray-400 text-sm">USD</div></div>
                      <select className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white">
                        <option>USD</option><option>EUR</option><option>GBP</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div><div className="text-white font-medium">Time Zone</div><div className="text-gray-400 text-sm">Europe/Berlin</div></div>
                      <span className="text-cyan-400">Auto-detected</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div><div className="text-white font-medium">Dark Mode</div><div className="text-gray-400 text-sm">Always on</div></div>
                      <span className="text-cyan-400">✓ Enabled</span>
                    </div>
                  </div>
                </GlassCard>

                {/* Connection Manager with Test/Sync */}
                <GlassCard className="p-6">
                  <ConnectionManager 
                    onImportTrades={async (connectionId, trades, platform) => {
                      if (!activeAccount?.id) {
                        alert('Please select an account first to import trades');
                        return;
                      }
                      
                      // Import trades via API
                      const result = await journalApiFetch(`/api/journal/import/${connectionId}`, {
                        method: 'POST',
                        body: JSON.stringify({
                          accountId: activeAccount.id,
                          trades: trades.map(t => ({
                            ...t,
                            source: platform,
                          })),
                        }),
                      });
                      
                      if (result.success) {
                        alert(`✅ Imported ${result.imported} trades! (${result.skipped} duplicates skipped)`);
                        refetchAll();
                      } else {
                        alert(`❌ Import failed: ${result.error || 'Unknown error'}`);
                      }
                    }}
                  />
                </GlassCard>

                {/* Quick Connect Buttons */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">➕ Add New Connection</h3>
                  <p className="text-gray-400 mb-4">Connect more trading platforms</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { name: 'MetaTrader 5', icon: '📊', key: 'mt5' },
                      { name: 'MetaTrader 4', icon: '📈', key: 'mt4' },
                      { name: 'BinaryFaster', icon: '⚡', key: 'binaryfaster' },
                      { name: 'Binance', icon: '🟡', key: 'binance' },
                      { name: 'Bybit', icon: '🟠', key: 'bybit' },
                      { name: 'cTrader', icon: '💜', key: 'ctrader' },
                      { name: 'TradingView', icon: '📺', key: 'tradingview' },
                      { name: 'Interactive Brokers', icon: '🏛️', key: 'ibkr' },
                      { name: 'TradeLocker', icon: '🔐', key: 'tradelocker' },
                    ].map(p => (
                      <button 
                        key={p.key} 
                        onClick={() => { setConnectPlatform(p.name); setShowConnectModal(true); }}
                        className="p-3 border border-white/10 rounded-xl text-sm transition-all bg-white/5 hover:bg-white/10 text-white"
                      >
                        <span className="mr-1">{p.icon}</span> {p.name}
                      </button>
                    ))}
                  </div>
                </GlassCard>
              </div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {showAddTrade && activeAccount && (
        <AddTradeModal accountId={activeAccount.id} onClose={() => setShowAddTrade(false)} onSuccess={refetchAll} />
      )}
      {showAddAccount && (
        <AddAccountModal onClose={() => setShowAddAccount(false)} onSuccess={refetchAll} />
      )}
      {showConnectModal && connectPlatform && (
        <PlatformConnectModal 
          platform={connectPlatform} 
          onClose={() => { setShowConnectModal(false); setConnectPlatform(null); }}
          onSuccess={(p) => setConnections({ ...connections, [p]: true })}
        />
      )}

      {/* Ask K.I.T. AI Chat */}
      {user && (
        <AskKitChat accountId={activeAccount?.id} userId={user.id} />
      )}
    </div>
  )
}
